import { z } from "zod";

export const MigrationOpportunityVersionSchema = z.object({
  version: z.string(),
  releaseDate: z.string().nullable().optional(),
  adoptionPercentage: z.number().nullable().optional(),
  laterVersionsAdoptionPercentage: z.number().nullable().optional(),
});

export type MigrationOpportunityVersionSchemaType = z.infer<
  typeof MigrationOpportunityVersionSchema
>;

export const MigrationOpportunityDependencyUpgradeSchema = z.object({
  type: z.literal("dependency_major_version_upgrade"),
  importerPath: z.string(),
  purl: z.string(),
  usageCount: z.number().nullable().optional(),
  currentVersion: MigrationOpportunityVersionSchema,
  latestVersion: MigrationOpportunityVersionSchema,
  recommendedVersion: MigrationOpportunityVersionSchema,
  dependencyEdges: z.string().array(),
});

export type MigrationOpportunityDependencyUpgradeSchemaType = z.infer<
  typeof MigrationOpportunityDependencyUpgradeSchema
>;

const EcosystemSchema = z.union([
  z.literal("Go"),
  z.literal("npm"),
  z.literal("JavaScript"),
  z.literal("OSS-Fuzz"),
  z.literal("PyPI"),
  z.literal("Python"),
  z.literal("RubyGems"),
  z.literal("crates.io"),
  z.literal("Packagist"),
  z.literal("NuGet"),
  z.literal("Linux"),
  z.literal("Hex"),
  z.literal("Android"),
  z.literal("GitHub Actions"),
  z.literal("Pub"),
  z.literal("ConanCenter"),
  z.literal("DWF"),
  z.literal("GSD"),
  z.literal("UVI"),
  z.literal("Hackage"),
  z.literal("GHC"),
  z.literal("Bitnami"),
  z.literal("CRAN"),
  z.literal("Bioconductor"),
  z.literal("SwiftURL"),
  z.object({
    type: z.literal("Maven"),
    repository: z.string(),
  }),
  z.object({
    type: z.literal("Debian"),
    version: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal("Alpine"),
    version: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal("Rocky Linux"),
    release: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal("AlmaLinux"),
    release: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal("Photon OS"),
    release: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal("Ubuntu"),
    version: z.string(),
    pro: z.boolean(),
    lts: z.boolean(),
  }),
]);

const PackageSchema = z.object({
  name: z.string(),
  ecosystem: EcosystemSchema,
  purl: z.string().nullable().optional(),
});

const RangeTypeSchema = z.enum(["UNSPECIFIED", "GIT", "SEMVER", "ECOSYSTEM"]);

const EventSchema = z.union([
  z.object({ introduced: z.string() }),
  z.object({ fixed: z.string() }),
  z.object({ last_affected: z.string() }),
  z.object({ limit: z.string() }),
]);

const RangeSchema = z.object({
  type: RangeTypeSchema,
  repo: z.string().nullable().optional(),
  events: z.array(EventSchema),
});

const SeverityTypeSchema = z.enum([
  "UNSPECIFIED",
  "CVSS_V4",
  "CVSS_V3",
  "CVSS_V2",
]);

const SeveritySchema = z.object({
  type: SeverityTypeSchema,
  score: z.string(),
});

const AffectedSchema = z.object({
  package: PackageSchema.nullable().optional(),
  severity: z.array(SeveritySchema).nullable().optional(),
  ranges: z.array(RangeSchema).nullable().optional(),
  versions: z.array(z.string()).nullable().optional(),
  ecosystem_specific: z.any().nullable().optional(),
  database_specific: z.any().nullable().optional(),
});

const ReferenceTypeSchema = z.enum([
  "UNDEFINED",
  "WEB",
  "ADVISORY",
  "REPORT",
  "FIX",
  "PACKAGE",
  "ARTICLE",
  "DETECTION",
  "INTRODUCED",
  "EVIDENCE",
  "GIT",
  "DISCUSSION",
]);

const ReferenceSchema = z.object({
  type: ReferenceTypeSchema,
  url: z.string(),
});

const CreditTypeSchema = z.enum([
  "FINDER",
  "REPORTER",
  "ANALYST",
  "COORDINATOR",
  "REMEDIATION_DEVELOPER",
  "REMEDIATION_REVIEWER",
  "REMEDIATION_VERIFIER",
  "TOOL",
  "SPONSOR",
  "OTHER",
]);

const CreditSchema = z.object({
  name: z.string(),
  contact: z.array(z.string()).nullable().optional(),
  credit_type: CreditTypeSchema.nullable().optional(),
});

export const MigrationOpportunityVulnerabilitySchema = z.object({
  type: z.literal("OSV"),
  schema_version: z.string().nullable().optional(),
  id: z.string(),
  published: z.string(),
  modified: z.string(),
  withdrawn: z.string().nullable().optional(),
  aliases: z.array(z.string()).nullable().optional(),
  related: z.array(z.string()).nullable().optional(),
  summary: z.string().nullable().optional(),
  details: z.string().nullable().optional(),
  affected: z.array(AffectedSchema),
  references: z.array(ReferenceSchema).nullable().optional(),
  severity: z.array(SeveritySchema).nullable().optional(),
  credits: z.array(CreditSchema).nullable().optional(),
  database_specific: z.any().nullable().optional(),
});

export const MigrationOpportunityDependencyVulnerabilitySchema = z.object({
  type: z.literal("dependency_vulnerability_upgrade"),
  purl: z.string(),
  importerPath: z.string(),
  usageCount: z.number().nullable().optional(),
  currentVersion: MigrationOpportunityVersionSchema,
  latestVersion: MigrationOpportunityVersionSchema,
  dependencyEdges: z.string().array(),
  vulnerabilities: z.array(MigrationOpportunityVulnerabilitySchema),
});

export type MigrationOpportunityDependencyVulnerabilitySchemaType = z.infer<
  typeof MigrationOpportunityVulnerabilitySchema
>;

export const LocalizationOpportunitySchema = z.object({
  type: z.literal("localization"),
  path: z.string(),
  count: z.number(),
  framework: z.string().optional().nullable(),
});

export const MigrationOpportunitySchema = z.union([
  MigrationOpportunityDependencyVulnerabilitySchema,
  MigrationOpportunityDependencyUpgradeSchema,
  LocalizationOpportunitySchema,
]);

export type MigrationOpportunitySchemaType = z.infer<
  typeof MigrationOpportunitySchema
>;
